import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { Popover, PopoverAnchor, PopoverContent } from '../Popover';
import { Time, TimePicker } from './TimePicker';

const timeRegex = /^([0-1]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
export function TimeInput({
  time,
  className,
  onChange,
  hasError,
  size = 8,
}: {
  className?: string;
  time?: Time;
  size?: number;
  onChange: (time?: Time) => void;
  hasError?: (error?: boolean) => void;
}) {
  const [timeString, setTimeString] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const skipResetInput = useRef<boolean>(false);

  useEffect(() => {
    if (time) {
      const timeStr = convertTimeToString(time);
      setTimeString(timeStr);
    } else {
      if (skipResetInput.current) {
        skipResetInput.current = false;
      } else {
        setTimeString('');
      }
    }
  }, [time]);

  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTimeString(value);
    if (!value) {
      onChange(undefined);
      if (hasError) {
        hasError(true);
      }
    }
    if (value) {
      const isValid = timeRegex.test(value);
      if (!isValid) {
        // onChange(undefined);
        // skipResetInput.current = true;
        if (hasError) {
          hasError(true);
          return;
        }
      } else {
        const time = convertStringToTime(value);
        onChange(time);
        if (hasError) {
          hasError(false);
        }
      }
    }
  };

  const handleTimePickerChange = (time: Time) => {
    onChange(time);
    setIsOpen(false);
    if (hasError) {
      hasError(false);
    }
  };

  const convertStringToTime = (timeStr: string): Time => {
    const [hour, minute, second] = timeStr.split(':').map(Number);
    return {
      hour,
      minute,
      second: isNaN(second) ? undefined : second,
    };
  };
  const convertTimeToString = (time: Time) => {
    if (!time) return '';
    return `${time.hour.toString().padStart(2, '0')}:${time.minute
      .toString()
      .padStart(2, '0')}:${time.second?.toString().padStart(2, '0')}`;
  };
  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
        <PopoverAnchor asChild>
          <div className={cn(`relative max-w-[160px]`, className)}>
            <Input
              className={`pr-8 h-${size}`}
              type="text"
              placeholder="HH:mm:ss"
              onChange={handleTimeInputChange}
              value={timeString}
            />
            <Button
              onClick={() => setIsOpen(true)}
              size={'icon'}
              variant={'link'}
              className={` h-${size} text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer`}
            >
              <Clock className="size-4" />
            </Button>
          </div>
        </PopoverAnchor>
        <PopoverContent className="grid w-auto p-2" align="start">
          <TimePicker time={time} onTimeChange={handleTimePickerChange} />
        </PopoverContent>
      </Popover>
    </>
  );
}
